import { useState } from "react";
import { motion } from "framer-motion";
import { Phone, Mail, MapPin, MessageCircle, CheckCircle2, Loader2, AlertCircle, Facebook, Instagram, Linkedin, Building2 } from "lucide-react";
import Seo from "@/components/Seo";
import PageHero from "@/components/common/PageHero";
import WhatsAppButton from "@/components/common/WhatsAppButton";
import { Reveal } from "@/components/common/Reveal";
import { enquiriesService } from "@/services/enquiriesService";
import { useFormGuard } from "@/components/common/FormGuard";
import { useSite, useWaLink } from "@/context/SiteDataContext";

const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRe = /^[+\d][\d\s()-]{6,}$/;

const TikTok = (props) => (<svg viewBox="0 0 24 24" fill="currentColor" {...props}><path d="M16.5 3c.3 2.1 1.6 3.7 3.5 4v2.3c-1.3 0-2.5-.3-3.5-.9v6.2c0 3.1-2.5 5.6-5.6 5.6S5.3 17.7 5.3 14.6 7.8 9 10.9 9c.3 0 .6 0 .9.1v2.4c-.3-.1-.6-.2-.9-.2-1.7 0-3.2 1.4-3.2 3.2s1.4 3.2 3.2 3.2 3.2-1.4 3.2-3.2V3h2.4z"/></svg>);

export default function Contact() {
  const SITE = useSite();
  const waLink = useWaLink();
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle");
  const [errorMessage, setErrorMessage] = useState("");
  const { withGuard, field: honeypot, reset: resetGuard } = useFormGuard();

  const set = (k, v) => { setForm((f) => ({ ...f, [k]: v })); setErrors((e) => ({ ...e, [k]: undefined })); };
  const inputCls = (k) => `w-full px-4 py-3 border rounded-md outline-none transition-all focus:ring-2 focus:ring-aqua/20 ${errors[k] ? "border-destructive" : "border-border focus:border-aqua"}`;

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name.";
    if (!form.email.trim()) e.email = "Please enter your email.";
    else if (!emailRe.test(form.email)) e.email = "Please enter a valid email.";
    if (!form.phone.trim()) e.phone = "Please enter your number.";
    else if (!phoneRe.test(form.phone)) e.phone = "Please enter a valid number.";
    if (!form.company.trim()) e.company = "Please enter your company or organisation.";
    if (!form.message.trim()) e.message = "Please enter a message.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setStatus("submitting");
    setErrorMessage("");
    try {
      await enquiriesService.submitContactForm(withGuard(form));
      setStatus("success");
      setForm({ name: "", email: "", phone: "", company: "", message: "" });
      resetGuard();
    } catch (error) {
      // Surface the server's reason (rate limit, validation) rather than a
      // generic failure — it tells the sender what to do next.
      setErrorMessage(error?.message || "");
      setStatus("error");
    }
  };

  const details = [
    { icon: MapPin, label: "Address", value: SITE.address },
    { icon: Phone, label: "Mobile", value: SITE.mobile, href: `tel:${SITE.mobile}` },
    { icon: Mail, label: "Email", value: SITE.email, href: `mailto:${SITE.email}` },
    { icon: Building2, label: "UEN", value: SITE.uen },
  ];

  return (
    <>
      <Seo routeKey="contact" title="Contact Us" description="Get in touch with Athena Scientific — WhatsApp, email or send us a message. We're based in Singapore." canonical={`https://${SITE.website}/contact`} />
      <PageHero eyebrow="Contact Us" title="Let's start with one question — how can we help?" description="Reach out by WhatsApp, email or the form below. Our team is ready to understand your objectives and recommend the right solution." crumbs={[{ label: "Contact Us" }]} />

      <section className="container-x py-16 md:py-24">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Reveal>
              <div className="space-y-5">
                {details.map((d) => (
                  <div key={d.label} className="flex gap-4">
                    <div className="w-11 h-11 rounded-md bg-aqua/10 flex items-center justify-center shrink-0"><d.icon className="w-5 h-5 text-aqua" strokeWidth={1.8} /></div>
                    <div>
                      <p className="text-xs uppercase tracking-widest font-semibold text-greyish">{d.label}</p>
                      {d.href ? <a href={d.href} className="text-ink font-medium hover:text-aqua transition-colors">{d.value}</a> : <p className="text-ink font-medium">{d.value}</p>}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 flex flex-wrap gap-3">
                <WhatsAppButton variant="solid" />
                <a href={`mailto:${SITE.email}`} data-testid="contact-email-btn" className="inline-flex items-center gap-2 border border-border px-6 py-3 rounded-md font-semibold text-ink hover:bg-[#f6f8f9] transition-all"><Mail className="w-5 h-5" /> Email Us</a>
              </div>

              <div className="mt-8">
                <p className="text-xs uppercase tracking-widest font-semibold text-greyish mb-3">Follow us</p>
                <div className="flex gap-3">
                  <a href={SITE.social.facebook} target="_blank" rel="noopener noreferrer" data-testid="contact-facebook" aria-label="Facebook" className="w-10 h-10 rounded-md border border-border flex items-center justify-center text-greyish hover:text-aqua hover:border-aqua transition-all"><Facebook className="w-4.5 h-4.5" /></a>
                  <a href={SITE.social.instagram} target="_blank" rel="noopener noreferrer" data-testid="contact-instagram" aria-label="Instagram" className="w-10 h-10 rounded-md border border-border flex items-center justify-center text-greyish hover:text-aqua hover:border-aqua transition-all"><Instagram className="w-4.5 h-4.5" /></a>
                  <a href={SITE.social.tiktok} target="_blank" rel="noopener noreferrer" data-testid="contact-tiktok" aria-label="TikTok" className="w-10 h-10 rounded-md border border-border flex items-center justify-center text-greyish hover:text-aqua hover:border-aqua transition-all"><TikTok className="w-4.5 h-4.5" /></a>
                  {SITE.social.linkedin && (
                    <a href={SITE.social.linkedin} target="_blank" rel="noopener noreferrer" data-testid="contact-linkedin" aria-label="LinkedIn" className="w-10 h-10 rounded-md border border-border flex items-center justify-center text-greyish hover:text-aqua hover:border-aqua transition-all"><Linkedin className="w-4.5 h-4.5" strokeWidth={1.8} /></a>
                  )}
                </div>
              </div>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              <div className="rounded-lg border border-border p-7 md:p-9 bg-white">
                {status === "success" ? (
                  <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="text-center py-10" data-testid="contact-success">
                    <div className="w-16 h-16 rounded-full bg-aqua/10 flex items-center justify-center mx-auto"><CheckCircle2 className="w-8 h-8 text-aqua" /></div>
                    <h3 className="mt-6 text-2xl font-bold text-ink">Thank you for your message.</h3>
                    <p className="mt-3 text-greyish">Our team will be in touch with you shortly.</p>
                    <button onClick={() => setStatus("idle")} className="mt-6 text-sm font-semibold text-aqua hover:underline">Send another message</button>
                  </motion.div>
                ) : (
                  <form onSubmit={submit} data-testid="contact-form" noValidate>
                    {honeypot}
                    {status === "error" && <div className="mb-5 flex items-center gap-2 p-4 rounded-md bg-destructive/10 text-destructive text-sm"><AlertCircle className="w-5 h-5 shrink-0" /> {errorMessage || "Something went wrong. Please try again."}</div>}
                    <div className="grid sm:grid-cols-2 gap-5">
                      <div><label className="block text-sm font-semibold text-ink mb-1.5">Name <span className="text-aqua">*</span></label><input value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name" className={inputCls("name")} placeholder="Your name" />{errors.name && <p className="mt-1.5 text-xs text-destructive">{errors.name}</p>}</div>
                      <div><label className="block text-sm font-semibold text-ink mb-1.5">Email <span className="text-aqua">*</span></label><input value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email" className={inputCls("email")} placeholder="you@company.com" />{errors.email && <p className="mt-1.5 text-xs text-destructive">{errors.email}</p>}</div>
                      <div><label className="block text-sm font-semibold text-ink mb-1.5">WhatsApp / Mobile <span className="text-aqua">*</span></label><input value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="contact-phone" className={inputCls("phone")} placeholder="+65 …" />{errors.phone && <p className="mt-1.5 text-xs text-destructive">{errors.phone}</p>}</div>
                      <div><label className="block text-sm font-semibold text-ink mb-1.5">Company <span className="text-aqua">*</span></label><input value={form.company} onChange={(e) => set("company", e.target.value)} data-testid="contact-company" className={inputCls("company")} placeholder="Your organisation" />{errors.company && <p className="mt-1.5 text-xs text-destructive">{errors.company}</p>}</div>
                    </div>
                    <div className="mt-5"><label className="block text-sm font-semibold text-ink mb-1.5">Message <span className="text-aqua">*</span></label><textarea value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message" rows={5} className={`${inputCls("message")} resize-none`} placeholder="How can we help?" />{errors.message && <p className="mt-1.5 text-xs text-destructive">{errors.message}</p>}</div>
                    <button type="submit" disabled={status === "submitting"} data-testid="contact-submit" className="mt-6 inline-flex items-center justify-center gap-2 bg-aqua text-white px-8 py-3.5 rounded-md font-semibold hover:bg-aqua-dark transition-all disabled:opacity-70">
                      {status === "submitting" ? <><Loader2 className="w-5 h-5 animate-spin" /> Sending…</> : "Send Message"}
                    </button>
                  </form>
                )}
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
